import { type FormEvent, useEffect, useState } from 'react';
import {
  completeSignup,
  getDownloadInfo,
  getPurchaseSession,
  type DownloadResult,
  type LeadRequestType,
  type PurchaseSession,
  submitWebsiteLead,
  verifyLicenseDownload,
} from '../../api';
import publicPlans from '../../config/moor-apps-public-plans.json';
import { type MoorAppsPlan, type MoorAppsPlanId } from '../../pricing';
import { TemplateProjectPage, TemplatesPage } from '../Templates/Component';
import { WebsitesPage } from '../WebsiteLibrary/Component';

type LeadState = 'idle' | 'sending' | 'sent' | 'failed';
type CommerceState = 'idle' | 'sending' | 'sent' | 'failed';
type InquirySummary = {
  requestType: LeadRequestType;
  name: string;
  email: string;
  phone: string;
  interest: string;
  teamSize: string;
  message: string;
  sentAt: string;
};

type ShowcaseCard = {
  name: string;
  category: string;
  summary: string;
  details: string[];
  imageSrc: string;
  alt: string;
};

type FeatureCard = {
  title: string;
  body: string;
};

type Metric = {
  value: string;
  label: string;
};

type MotionReel = {
  title: string;
  label: string;
  src: string;
  poster: string;
};

type DocumentationCard = {
  title: string;
  body: string;
};

const mediaAssets = {
  buildPlanning: '/media/moor-build-planning.png',
  labWorkspace: '/media/moor-lab-workspace.png',
  readyToRun: '/media/moor-ready-to-run.png',
  scheduler: '/media/moor-scheduler.png',
  commercialLoop: '/media/moor-commercial.mp4',
  workflowLoop: '/media/moor-workflow-loop.mp4',
  labLoop: '/media/moor-lab-loop.mp4',
  scheduleLoop: '/media/moor-schedule-loop.mp4',
  productInAction: '/media/product-in-action.mp4',
} as const;

const navigationItems = [
  { label: 'Websites', href: '/websites' },
  { label: 'Offer', href: '#contact' },
  { label: 'Proof', href: '#proof' },
  { label: 'Workflow', href: '#command-center' },
  { label: 'Screens', href: '#screens' },
  { label: 'Pricing', href: '#pricing' },
] as const;

const publicPricingPlans = publicPlans as MoorAppsPlan[];

const metrics = [
  { value: '3', label: 'messy backlog items turned into AI-ready work orders first' },
  { value: '20 min', label: 'setup call for small dev agencies and founder-led teams' },
  { value: '1', label: 'queue for ticket, repo, branch, model, prompt, and reviewer context' },
  { value: '0', label: 'blind handoffs before an AI coding agent touches client code' },
] as const satisfies readonly Metric[];

const showcaseCards = [
  {
    name: 'Build Planning',
    category: 'Work order execution',
    summary:
      'Turn a loose client request into a run-ready brief with the right ticket, repository, branch, model, reviewer, and instructions.',
    details: ['Client queue', 'Git state', 'Model prompt'],
    imageSrc: mediaAssets.buildPlanning,
    alt: 'Moor Build screen showing a planning queue work order with live context, Slack, and GitHub panels.',
  },
  {
    name: 'Lab Workspace',
    category: 'Intake and triage',
    summary:
      'Filter the backlog by client, status, due date, readiness, and review state so only safe work reaches the agent queue.',
    details: ['Client filters', 'Ready count', 'Review lanes'],
    imageSrc: mediaAssets.labWorkspace,
    alt: 'Moor Lab Jira Workspace showing queue metrics, issue cards, and integration panels.',
  },
  {
    name: 'Ready to Run',
    category: 'Preflight validation',
    summary:
      'Confirm that each work order is complete, targeted to the right project, and paired with the right AI implementor before execution.',
    details: ['Ready queue', 'Branch setup', 'Model choice'],
    imageSrc: mediaAssets.readyToRun,
    alt: 'Moor Build screen showing two ready work orders configured for implementation.',
  },
  {
    name: 'Scheduler',
    category: 'Planned AI capacity',
    summary:
      'Batch related client tasks, schedule agent runs, and keep implementation timing visible to the people reviewing the output.',
    details: ['Week view', 'Client batches', 'Task scheduling'],
    imageSrc: mediaAssets.scheduler,
    alt: 'Moor Scheduler screen showing July 2026 calendar planning and live integration panels.',
  },
] as const satisfies readonly ShowcaseCard[];

const featureCards = [
  {
    title: 'Choose what is safe to delegate',
    body:
      'Moor helps an agency operator separate clear implementation tasks from vague client asks before a coding agent starts making changes.',
  },
  {
    title: 'Attach the missing delivery context',
    body:
      'Each task carries the repo, local path, branch, language, model, implementor, schedule, reviewer, and prompt in one place.',
  },
  {
    title: 'Review output without reconstructing the run',
    body:
      'Runs stream progress, preserve output in History, and keep validation messages visible so the handoff is reviewable after the agent finishes.',
  },
] as const satisfies readonly FeatureCard[];

const motionReels = [
  {
    title: 'Setup Call Preview',
    label: 'See the product before purchase',
    src: mediaAssets.commercialLoop,
    poster: mediaAssets.labWorkspace,
  },
  {
    title: 'Work Order Movement',
    label: 'Request, validation, execution, review',
    src: mediaAssets.workflowLoop,
    poster: mediaAssets.buildPlanning,
  },
  {
    title: 'Backlog Intake',
    label: 'Client ticket flow',
    src: mediaAssets.labLoop,
    poster: mediaAssets.labWorkspace,
  },
  {
    title: 'Scheduled Runs',
    label: 'Batch agent work by day',
    src: mediaAssets.scheduleLoop,
    poster: mediaAssets.scheduler,
  },
] as const satisfies readonly MotionReel[];

const contextPanels = [
  { location: '1. Bring three tasks', note: 'Start with real client bugs, chores, or product fixes instead of a fake demo script.' },
  { location: '2. Map the context', note: 'Attach the ticket, repo, local folder, branch, language, model, implementor, reviewer, and prompt.' },
  { location: '3. Validate before running', note: 'Moor shows what is missing before the AI agent touches code.' },
  { location: '4. Review the result', note: 'Use progress, logs, and History to inspect the run before the next agent pass.' },
] as const;

const audienceCards = [
  {
    title: 'Client fixes arrive half-baked',
    body:
      'Moor turns rough client requests into specific agent-ready instructions with the missing repo, branch, and review details filled in.',
  },
  {
    title: 'Repos and branches multiply',
    body:
      'Keep each task tied to the right project folder, branch, model, and reviewer so agent work does not drift across client codebases.',
  },
  {
    title: 'AI output still needs a handoff',
    body:
      'History, logs, validation state, and review context stay attached to the run so the team can inspect what happened.',
  },
] as const satisfies readonly DocumentationCard[];

function getFormMessageClassName(state: LeadState) {
  return `formMessage formMessage-${state}`;
}

function getSubmitLabel(state: LeadState, requestType: LeadRequestType) {
  if (state === 'sending') {
    return 'Sending...';
  }

  return requestType === 'contact' ? 'Send question' : 'Book setup call';
}

function getCommerceMessageClassName(state: CommerceState) {
  return `formMessage formMessage-${state}`;
}

function getSessionIdFromLocation() {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('session_id') || '';
}

function getLicenseKeyFromLocation() {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('license_key') || '';
}

function getCurrentPathname() {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname;
}

function getInquirySummary(): InquirySummary | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem('moorInquirySummary');
    return raw ? (JSON.parse(raw) as InquirySummary) : null;
  } catch {
    return null;
  }
}

function saveInquirySummary(summary: InquirySummary) {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem('moorInquirySummary', JSON.stringify(summary));
}

function getRequestTypeTitle(requestType?: LeadRequestType) {
  return requestType === 'contact' ? 'Contact request' : 'Demo request';
}

function isCompanyRequired(planId?: MoorAppsPlanId) {
  return planId === 'business' || planId === 'enterprise';
}

function getLicenseEmailStatus(purchase?: DownloadResult['purchase']) {
  const status = purchase?.license_email_status || purchase?.access_email_status || '';
  if (!purchase) return 'Loading...';
  return status === 'failed' ? 'Email needs support' : 'Emailed';
}

function getDownloadPlanLabel(
  purchase?: DownloadResult['purchase'],
  license?: DownloadResult['license'],
) {
  return purchase?.plan_name || license?.planName || 'License required';
}

function getDownloadAccountLabel(
  purchase?: DownloadResult['purchase'],
  license?: DownloadResult['license'],
) {
  return purchase?.customer_email || license?.accountEmail || 'Shown after verification';
}

function PricingSection() {
  const checkoutWasCanceled =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('checkout') === 'canceled';
  const checkoutState: CommerceState = checkoutWasCanceled ? 'failed' : 'idle';
  const checkoutMessage = checkoutWasCanceled
    ? 'Checkout was canceled. You have not been charged.'
    : '';

  return (
    <section className="pricingSection" id="pricing" aria-labelledby="pricing-title">
      <div className="sectionShell">
        <div className="pricingIntro">
          <p className="eyebrow">Moor Engineers pricing</p>
          <h2 id="pricing-title">Start with proof, then choose the license that fits.</h2>
          <p>
            The setup call is the front door. Once Moor is useful for your queue,
            choose the desktop license that fits your agency workflow.
          </p>
          <div className="pricingSignals" aria-label="Checkout safeguards">
            <span>20-minute setup first</span>
            <span>Bring three real tasks</span>
            <span>Stripe-hosted checkout</span>
          </div>
        </div>

        <div className="pricingGrid">
          {publicPricingPlans.map((plan) => (
            <article
              className={`pricingCard${plan.badge ? ' pricingCardFeatured' : ''}`}
              key={plan.id}
            >
              {plan.badge ? <span className="planBadge">{plan.badge}</span> : null}
              <div className="planHeader">
                <h3>{plan.name}</h3>
                <p>{plan.description}</p>
              </div>
              <div className="planPrice">
                <strong>{plan.displayPrice}</strong>
                <span>{plan.priceDetail}</span>
              </div>
              <div className="pricingButtonGroup">
                {(plan.paymentLinks?.length ? plan.paymentLinks : []).map((paymentLink) => (
                  <a
                    className="button buttonPrimary pricingButton"
                    href={paymentLink.url}
                    key={paymentLink.url}
                  >
                    {paymentLink.label || plan.ctaText}
                  </a>
                ))}
              </div>
              <div className="planFeatureGroup">
                <span>Included</span>
                <ul>
                  {plan.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <p className={getCommerceMessageClassName(checkoutState)} aria-live="polite">
          {checkoutMessage}
        </p>
      </div>
    </section>
  );
}

function SignupPage() {
  const [sessionId] = useState(getSessionIdFromLocation);
  const [purchase, setPurchase] = useState<PurchaseSession | null>(null);
  const [status, setStatus] = useState<CommerceState>('sending');
  const [message, setMessage] = useState('Verifying your Stripe payment...');
  const [submitState, setSubmitState] = useState<CommerceState>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function verifyPurchase() {
      const result = await getPurchaseSession(sessionId);

      if (!isMounted) return;

      if (!result.ok || !result.purchase) {
        setStatus('failed');
        setMessage(result.message);
        return;
      }

      setPurchase(result.purchase);
      setStatus('sent');
      setMessage('Payment verified. Complete account setup to receive your access code.');
    }

    verifyPurchase();

    return () => {
      isMounted = false;
    };
  }, [sessionId]);

  async function handleSignup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!purchase) {
      setSubmitState('failed');
      setSubmitMessage('Payment verification is required before account setup.');
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const firstName = String(formData.get('firstName') || '');
    const lastName = String(formData.get('lastName') || '');
    const email = String(formData.get('email') || '');
    const company = String(formData.get('company') || '');

    if (isCompanyRequired(purchase.plan_id) && !company.trim()) {
      setSubmitState('failed');
      setSubmitMessage('Enter your company or organization name.');
      return;
    }

    setSubmitState('sending');
    setSubmitMessage('');

    const result = await completeSignup({
      sessionId,
      firstName,
      lastName,
      email,
      company,
    });

    if (result.ok && result.downloadUrl) {
      window.location.assign(result.downloadUrl);
      return;
    }

    setSubmitState('failed');
    setSubmitMessage(result.message);
  }

  return (
    <main className="moorSite onboardingPage">
      <section className="onboardingShell" aria-labelledby="signup-title">
        <a className="brandLockup onboardingBrand" href="/" aria-label="Moor Engineers home">
          <img className="brandMark" src="/moor-engineers-logo.png" alt="" />
          <span>
            <strong>Moor Engineers</strong>
            <small>Account setup</small>
          </span>
        </a>

        <div className="onboardingGrid">
          <div className="onboardingCopy">
            <p className="eyebrow">Payment verified signup</p>
            <h1 id="signup-title">
              <span>Set up</span>
              <span>your Moor Engineers</span>
              <span>account.</span>
            </h1>
            <p>
              Account provisioning starts only after the backend verifies the Stripe
              Checkout Session. The purchased plan is locked to the verified transaction.
            </p>
            <div className="planSummary">
              <span>Purchased plan</span>
              <strong>{purchase ? purchase.plan_name : 'Verifying...'}</strong>
              {purchase ? <small>{purchase.display_price}</small> : null}
            </div>
            <p className={getCommerceMessageClassName(status)} aria-live="polite">
              {message}
            </p>
          </div>

          <form className="contactForm signupForm" onSubmit={handleSignup}>
            <label>
              First name
              <input name="firstName" autoComplete="given-name" required disabled={!purchase} />
            </label>
            <label>
              Last name
              <input name="lastName" autoComplete="family-name" required disabled={!purchase} />
            </label>
            <label>
              Email
              <input
                name="email"
                type="email"
                autoComplete="email"
                defaultValue={purchase?.customer_email || ''}
                required
                disabled={!purchase}
              />
            </label>
            <label>
              Company / Organization
              <input
                name="company"
                autoComplete="organization"
                required={isCompanyRequired(purchase?.plan_id)}
                disabled={!purchase}
              />
            </label>
            <label className="wide">
              Purchased plan
              <input value={purchase ? purchase.plan_name : 'Verifying purchase'} readOnly />
            </label>
            <button
              className="button buttonPrimary"
              type="submit"
              disabled={!purchase || submitState === 'sending'}
            >
              {submitState === 'sending' ? 'Provisioning account...' : 'Complete account setup'}
            </button>
            <p className={getCommerceMessageClassName(submitState)} aria-live="polite">
              {submitMessage}
            </p>
          </form>
        </div>
      </section>
    </main>
  );
}

function DownloadPage() {
  const [sessionId] = useState(getSessionIdFromLocation);
  const [initialLicenseKey] = useState(getLicenseKeyFromLocation);
  const [purchaseInfo, setPurchaseInfo] = useState<DownloadResult | null>(null);
  const [activatedDownloadInfo, setActivatedDownloadInfo] = useState<DownloadResult | null>(null);
  const [status, setStatus] = useState<CommerceState>('sending');
  const [message, setMessage] = useState(
    sessionId
      ? 'Checking your Stripe purchase...'
      : 'Paste your license key to unlock the Moor Engineers download.',
  );
  const [activationState, setActivationState] = useState<CommerceState>('idle');
  const [activationMessage, setActivationMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadDownload() {
      const result = await getDownloadInfo(sessionId);

      if (!isMounted) return;

      setPurchaseInfo(result);
      setStatus(result.ok ? 'sent' : 'failed');
      setMessage(result.message);
    }

    loadDownload();

    return () => {
      isMounted = false;
    };
  }, [sessionId]);

  async function handleLicenseActivation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(form);
    const licenseKey = String(formData.get('licenseKey') || '');

    setActivationState('sending');
    setActivationMessage('');

    const result = await verifyLicenseDownload(licenseKey);
    setActivatedDownloadInfo(result);
    setActivationState(result.ok ? 'sent' : 'failed');
    setActivationMessage(result.message);
  }

  const purchase = purchaseInfo?.purchase;
  const license = activatedDownloadInfo?.license;
  const downloads = activatedDownloadInfo?.downloads || [];
  const supportEmail =
    activatedDownloadInfo?.supportEmail ||
    purchaseInfo?.supportEmail ||
    'clintonjohnson@cmjsoftware.com';
  const isUnlocked = activationState === 'sent' && activatedDownloadInfo?.ok;

  return (
    <main className="moorSite onboardingPage">
      <section className="onboardingShell" aria-labelledby="download-title">
        <a className="brandLockup onboardingBrand" href="/" aria-label="Moor Engineers home">
          <img className="brandMark" src="/moor-engineers-logo.png" alt="" />
          <span>
            <strong>Moor Engineers</strong>
            <small>Download</small>
          </span>
        </a>

        <div className="downloadPanel">
          <p className="eyebrow">Download software</p>
          <h1 id="download-title">
            <span>Unlock</span>
            <span>Moor Engineers.</span>
          </h1>
          <p>
            Thank you for purchasing Moor Engineers. Paste the license key from your purchase
            email to verify your active subscription and unlock the installer.
          </p>
          <div className="emailDeliveryNotice" role="note">
            If the license email is not in your inbox within a few minutes, check your
            Spam or Junk folder for Moor Engineers.
          </div>

          <div className="downloadDetails">
            <article>
              <span>Plan</span>
              <strong>{getDownloadPlanLabel(purchase, license)}</strong>
              {purchase ? <small>{purchase.display_price}</small> : null}
            </article>
            <article>
              <span>Account</span>
              <strong>{getDownloadAccountLabel(purchase, license)}</strong>
              <small>{purchase ? getLicenseEmailStatus(purchase) : 'License email required'}</small>
            </article>
            <article>
              <span>License status</span>
              <strong>{isUnlocked ? 'Verified active' : 'Waiting for key'}</strong>
              <small>{license?.licenseId || 'Paste your license key below'}</small>
            </article>
            <article>
              <span>Downloads</span>
              <strong>
                {isUnlocked
                  ? downloads.length > 0
                    ? 'Ready'
                    : 'Not configured'
                  : 'Locked'}
              </strong>
              <small>{isUnlocked ? 'Installer access checked' : 'Server-side validation required'}</small>
            </article>
          </div>

          <form className="contactForm licenseUnlockForm" onSubmit={handleLicenseActivation}>
            <label className="wide">
              License key
              <input
                name="licenseKey"
                autoComplete="off"
                defaultValue={initialLicenseKey}
                placeholder="MOOR-LIC-..."
                required
              />
            </label>
            <button
              className="button buttonPrimary"
              type="submit"
              disabled={activationState === 'sending'}
            >
              {activationState === 'sending' ? 'Verifying license...' : 'Unlock download'}
            </button>
            <p className={getCommerceMessageClassName(activationState)} aria-live="polite">
              {activationMessage}
            </p>
          </form>

          <div className="downloadActions">
            {isUnlocked && downloads.length > 0 ? (
              downloads.map((download) => (
                <a className="button buttonPrimary" href={download.url} key={download.platform}>
                  {download.label}
                </a>
              ))
            ) : (
              <button className="button buttonSecondary" type="button" disabled>
                {isUnlocked ? 'Download unavailable' : 'Verify license to download'}
              </button>
            )}
            <a
              className="button buttonSecondary"
              href={`mailto:${supportEmail}`}
            >
              Contact support
            </a>
          </div>

          <ol className="installSteps">
            <li>Install Moor Engineers for your operating system.</li>
            <li>Open Moor Engineers and choose account activation.</li>
            <li>Enter your email address and the license key from your email.</li>
          </ol>

          <p className={getCommerceMessageClassName(status)} aria-live="polite">
            {message}
          </p>
        </div>
      </section>
    </main>
  );
}

function InquirySuccessPage() {
  const [summary] = useState(getInquirySummary);
  const requestTitle = getRequestTypeTitle(summary?.requestType);
  const submittedAt = summary?.sentAt
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(summary.sentAt))
    : 'Just now';

  return (
    <main className="moorSite onboardingPage inquirySuccessPage">
      <section className="onboardingShell inquirySuccessShell" aria-labelledby="inquiry-success-title">
        <a className="brandLockup onboardingBrand" href="/" aria-label="Moor Engineers home">
          <img className="brandMark" src="/moor-engineers-logo.png" alt="" />
          <span>
            <strong>Moor Engineers</strong>
            <small>Inquiry sent</small>
          </span>
        </a>

        <div className="inquirySuccessPanel">
          <p className="eyebrow">{requestTitle}</p>
          <h1 id="inquiry-success-title">
            <span>Your inquiry</span>
            <span>was sent.</span>
          </h1>
          <p>
            Thanks{summary?.name ? `, ${summary.name}` : ''}. Your message was delivered to
            Moor Engineers. A reply will come from clintonjohnson@cmjsoftware.com.
          </p>

          <div className="inquirySuccessGrid">
            <article>
              <span>Name</span>
              <strong>{summary?.name || 'Submitted'}</strong>
            </article>
            <article>
              <span>Email</span>
              <strong>{summary?.email || 'Reply address received'}</strong>
            </article>
            <article>
              <span>Product interest</span>
              <strong>{summary?.interest || 'Moor Engineers workflow'}</strong>
            </article>
            <article>
              <span>Team size</span>
              <strong>{summary?.teamSize || 'Not specified'}</strong>
            </article>
          </div>

          <div className="inquirySuccessMessage">
            <span>Submitted</span>
            <strong>{submittedAt}</strong>
            <p>
              If you forgot to include a repo, ticket link, integration detail, or a specific
              timeline, reply to the confirmation thread with that context so it can be added
              before the walkthrough.
            </p>
          </div>

          <div className="downloadActions">
            <a className="button buttonPrimary" href="/">
              Back to Moor Engineers
            </a>
            <a className="button buttonSecondary" href="/#contact">
              Send another inquiry
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

export function MoorEngineersComponent() {
  const [pathname, setPathname] = useState(getCurrentPathname);
  const [leadState, setLeadState] = useState<LeadState>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [requestType, setRequestType] = useState<LeadRequestType>('demo');

  useEffect(() => {
    function handlePopState() {
      setPathname(getCurrentPathname());
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  if (pathname === '/signup') {
    return <SignupPage />;
  }

  if (pathname === '/download') {
    return <DownloadPage />;
  }

  if (pathname === '/inquiry/sent') {
    return <InquirySuccessPage />;
  }

  if (pathname === '/templates' || pathname === '/templates/') {
    return <TemplatesPage />;
  }

  if (pathname === '/websites' || pathname === '/websites/') {
    return <WebsitesPage />;
  }

  if (pathname.startsWith('/websites/')) {
    return <WebsitesPage projectSlug={pathname.slice('/websites/'.length).replace(/\/$/, '')} />;
  }

  if (pathname.startsWith('/templates/')) {
    return <TemplateProjectPage projectSlug={pathname.slice('/templates/'.length).replace(/\/$/, '')} />;
  }

  async function handleProductInquiry(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLeadState('sending');

    const form = event.currentTarget;
    const formData = new FormData(form);
    const selectedRequestType = formData.get('requestType') === 'contact' ? 'contact' : 'demo';
    const product = String(formData.get('product') ?? 'Moor Engineers setup call');
    const volume = String(formData.get('volume') ?? 'Not specified');
    const message = String(formData.get('message') ?? '');
    const inquirySummary: InquirySummary = {
      requestType: selectedRequestType,
      name: String(formData.get('name') ?? ''),
      email: String(formData.get('email') ?? ''),
      phone: String(formData.get('phone') ?? ''),
      interest: product,
      teamSize: volume,
      message,
      sentAt: new Date().toISOString(),
    };

    const result = await submitWebsiteLead({
      requestType: selectedRequestType,
      name: inquirySummary.name,
      email: inquirySummary.email,
      phone: inquirySummary.phone,
      interest: product,
      teamSize: volume,
      company: String(formData.get('company') ?? ''),
      message,
    });

    setLeadState(result.ok ? 'sent' : 'failed');
    setFormMessage(result.message);

    if (result.ok) {
      saveInquirySummary(inquirySummary);
      form.reset();
      window.history.pushState({}, '', '/inquiry/sent');
      setPathname('/inquiry/sent');
      setLeadState('idle');
      setFormMessage('');
    }
  }

  return (
    <main className="moorSite" id="top">
      <header className="siteHeader">
        <a className="brandLockup" href="#top" aria-label="Moor Engineers home">
          <img className="brandMark" src="/moor-engineers-logo.png" alt="" />
          <span>
            <strong>Moor Engineers</strong>
            <small>AI work orders</small>
          </span>
        </a>

        <nav className="siteNav" aria-label="Primary navigation">
          {navigationItems.map((item) => (
            <a href={item.href} key={item.label}>
              {item.label}
            </a>
          ))}
        </nav>

        <a className="headerCta" href="#contact">
          Book setup call
        </a>
      </header>

      <section className="heroSection" aria-labelledby="hero-title">
        <video
          className="heroBackgroundVideo"
          src={mediaAssets.commercialLoop}
          poster={mediaAssets.labWorkspace}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        />
        <div className="sectionShell heroShell">
          <div className="heroCopy">
            <p className="eyebrow">For small dev agencies</p>
            <h1 id="hero-title">
              <span>Stop losing</span>
              <span>control of AI</span>
              <span>coding agents.</span>
            </h1>
            <p className="heroText">
              Moor turns messy client tickets, repo context, branch decisions, model
              choice, and review rules into validated work orders before an agent
              touches code. Bring three backlog items; leave with the first AI-ready
              queue.
            </p>
            <div className="heroActions">
              <a className="button buttonPrimary" href="#contact">
                Book 20-minute setup
              </a>
              <a className="button buttonSecondary" href="#proof">
                Watch proof
              </a>
              <a className="button buttonSecondary" href="#command-center">
                See workflow
              </a>
            </div>
          </div>

          <div className="heroSpecPanel" aria-label="Featured capture">
            <span>Setup offer</span>
            <strong>We build your first three work orders with you</strong>
            <p>
              In one short call, map real tickets to the right repo, branch, prompt,
              model, and review path so the agency can delegate without guessing.
            </p>
            <div className="signalRows" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
          </div>
        </div>

        <div className="metricBand" aria-label="Moor Engineers key metrics">
          {metrics.map((metric) => (
            <article key={metric.label}>
              <strong>{metric.value}</strong>
              <span>{metric.label}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="contactSection" id="contact" aria-labelledby="contact-title">
        <div className="sectionShell contactShell">
          <div className="contactCopy">
            <p className="eyebrow">20-minute setup call</p>
            <h2 id="contact-title">Bring three messy tickets. Leave with the first Moor queue.</h2>
            <p>
              This is for small dev agencies and founder-led teams using AI coding
              agents without enough structure. We will map real work to the correct
              repo, branch, model, prompt, and review path before anything runs.
            </p>
          </div>

          <form className="contactForm" onSubmit={handleProductInquiry}>
            <label>
              Request type
              <select
                name="requestType"
                value={requestType}
                onChange={(event) => setRequestType(event.currentTarget.value === 'contact' ? 'contact' : 'demo')}
              >
                <option value="demo">Book setup call</option>
                <option value="contact">Ask a question</option>
              </select>
            </label>
            <label>
              Name
              <input name="name" autoComplete="name" required />
            </label>
            <label>
              Email
              <input name="email" type="email" autoComplete="email" required />
            </label>
            <label>
              Phone
              <input name="phone" autoComplete="tel" />
            </label>
            <label>
              Biggest workflow pain
              <select name="product" defaultValue="Client bug queue">
                <option>Client bug queue</option>
                <option>Multi-repo handoffs</option>
                <option>AI agent review</option>
                <option>Full agency workflow</option>
              </select>
            </label>
            <label>
              Team size
              <select name="volume" defaultValue="1-3 person agency">
                <option>1-3 person agency</option>
                <option>4-10 person agency</option>
                <option>11+ person agency</option>
                <option>Solo builder</option>
              </select>
            </label>
            <label className="wide">
              What are the first three tickets or repo problems?
              <textarea
                name="message"
                rows={4}
                placeholder="Paste rough task titles, repo context, or the client work you want to delegate safely."
                required
              />
            </label>
            <label className="hiddenField" aria-hidden="true">
              Company
              <input name="company" tabIndex={-1} autoComplete="off" />
            </label>
            <button className="button buttonPrimary" type="submit" disabled={leadState === 'sending'}>
              {getSubmitLabel(leadState, requestType)}
            </button>
            <p className={getFormMessageClassName(leadState)} aria-live="polite">
              {formMessage}
            </p>
          </form>
        </div>
      </section>

      <section className="proofSection" id="proof" aria-labelledby="proof-title">
        <div className="proofCopy">
          <p className="eyebrow">Proof first</p>
          <h2 id="proof-title">Watch the workflow before anyone pays.</h2>
          <p>
            The sales motion is simple: bring three messy tickets, map them into real
            work orders, then review what Moor captured before a coding agent runs. No
            abstract demo. No pricing-first pressure.
          </p>
          <a className="button buttonDark" href="#contact">
            Book the setup call
          </a>
        </div>
        <div className="proofImage appProof" aria-label="Moor product walkthrough video">
          <video
            src={mediaAssets.productInAction}
            poster={mediaAssets.readyToRun}
            controls
            muted
            playsInline
            preload="metadata"
            aria-label="Moor product walkthrough from request to work order to review"
          />
        </div>
      </section>

      <section className="splitMediaSection" id="command-center">
        <div className="mediaWall" aria-label="Moor Engineers workflow video">
          <video
            className="mediaVideo"
            src={mediaAssets.workflowLoop}
            poster={mediaAssets.buildPlanning}
            autoPlay
            muted
            loop
            playsInline
            aria-label="Moor Engineers workflow loop"
          />
          <div className="videoFrame">
            <span className="playButton" aria-hidden="true" />
            <div>
              <strong>Workflow reel</strong>
              <p>Ticket intake, validation, execution, and review in one operating surface.</p>
            </div>
          </div>
        </div>
        <div className="problemCopy">
          <p className="eyebrow">The agency problem</p>
          <h2>AI agents can move fast. Agencies still own the handoff.</h2>
          <p>
            Client requests live in Jira, Slack, email, and calls. Code lives across
            multiple repos and local folders. AI models can make changes, but they still
            need the exact project, branch, constraints, and review expectations.
          </p>
          <p>
            Moor is the operating layer between the request and the coding agent. It
            turns "can you fix this?" into a work order your team can validate, run,
            inspect, and repeat.
          </p>
        </div>
      </section>

      <section className="engineeringSection" id="why" aria-labelledby="engineering-title">
        <div className="sectionShell">
          <div className="sectionIntro">
            <p className="eyebrow">The promise</p>
            <h2 id="engineering-title">Make the first three delegated tasks controlled and reviewable.</h2>
            <p>
              Start with three real backlog items. Moor keeps scope, project targeting,
              branch naming, model choice, schedule, logs, and review history visible so
              your agency can delegate work without losing control.
            </p>
          </div>

          <div className="featureGrid">
            {featureCards.map((card) => (
              <article className="featureCard" key={card.title}>
                <span aria-hidden="true" />
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="productsSection" id="screens" aria-labelledby="screens-title">
        <div className="sectionShell">
          <div className="sectionIntro productsIntro">
            <p className="eyebrow">Product surface</p>
            <h2 id="screens-title">The agency queue before the agent run.</h2>
            <p>
              Moor is not another chat box. It is a workspace for intake, planning,
              validation, scheduling, execution, and review, with real screens that show
              exactly how client work moves.
            </p>
          </div>

          <div className="productGrid">
            {showcaseCards.map((product) => (
              <article className="productCard" key={product.name}>
                <div className="productVisual appShot">
                  <img src={product.imageSrc} alt={product.alt} loading="lazy" />
                </div>
                <p>{product.category}</p>
                <h3>{product.name}</h3>
                <p>{product.summary}</p>
                <ul>
                  {product.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="testingSection" id="how-it-works" aria-labelledby="motion-title">
        <div className="sectionShell testingShell">
          <div className="sectionIntro">
            <p className="eyebrow">How it works</p>
            <h2 id="motion-title">From rough client ask to reviewable agent work.</h2>
            <p>
              Moor collects the scattered facts around a request, validates them as a
              work order, and launches the selected AI engineering runner with enough
              context to make a useful code change.
            </p>
          </div>
          <div className="motionGrid" aria-label="Moor Engineers video loops">
            {motionReels.map((reel) => (
              <article className="motionCard" key={reel.title}>
                <video src={reel.src} poster={reel.poster} autoPlay muted loop playsInline />
                <div>
                  <p>{reel.label}</p>
                  <h3>{reel.title}</h3>
                </div>
              </article>
            ))}
          </div>
          <div className="testList">
            {contextPanels.map((site) => (
              <article key={site.location}>
                <h3>{site.location}</h3>
                <p>{site.note}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="notesSection">
        <div className="sectionShell notesGrid">
          <div className="notesIntro">
            <p className="eyebrow">Small agency fit</p>
            <h2>Built for shops juggling client tickets, multiple repos, and AI output.</h2>
          </div>
          <article className="actionClipCard">
            <video
              src={mediaAssets.productInAction}
              poster={mediaAssets.readyToRun}
              controls
              muted
              playsInline
              preload="metadata"
              aria-label="Moor Product in Action walkthrough video"
            />
            <div className="actionClipCaption">
              <p>Product in action</p>
              <h3>Request to work order to review, without losing repo details.</h3>
              <span>
                The walkthrough shows connected context, visible queues, validated
                orders, and human review after the run.
              </span>
            </div>
          </article>
          <div className="noteStack">
            {audienceCards.map((card) => (
              <article className="noteCard" key={card.title}>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <PricingSection />

      <footer className="siteFooter">
        <div className="footerMain">
          <a className="brandLockup" href="#top" aria-label="Moor Engineers home">
            <img className="brandMark" src="/moor-engineers-logo.png" alt="" />
            <span>
              <strong>Moor Engineers</strong>
              <small>AI work orders</small>
            </span>
          </a>
          <nav aria-label="Footer navigation">
            {navigationItems.map((item) => (
              <a href={item.href} key={item.label}>
                {item.label}
              </a>
            ))}
          </nav>
        </div>

        <a
          className="cmjCallingCard"
          href="https://cmjsoftware.com/"
          target="_blank"
          rel="noreferrer"
          aria-label="Visit CMJ Software, website designer and developer"
        >
          <img
            className="cmjLogo"
            src="/cmj-software-logo.png"
            alt=""
            aria-hidden="true"
          />
          <span className="cmjCallingCardCopy">
            <small>Designed &amp; developed by</small>
            <strong>CMJ Software</strong>
            <span>Thoughtful software, websites, and digital experiences.</span>
          </span>
          <span className="cmjCallingCardLink">
            Visit studio
            <span aria-hidden="true">&#8599;</span>
          </span>
        </a>
      </footer>
    </main>
  );
}
